import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DoCheck,
  effect,
  forwardRef,
  inject,
  input,
  linkedSignal,
  signal,
  untracked,
} from "@angular/core";
import {
  ControlValueAccessor,
  FormGroupDirective,
  NgControl,
  NgForm,
} from "@angular/forms";
import type { ClassValue } from "clsx";
import {
  BrnFormFieldControl,
  ErrorStateMatcher,
  ErrorStateTracker,
  HlmButton,
  HlmIcon,
  HlmInput,
} from "@pagai-fe/ui";
import { NgIcon, provideIcons } from "@ng-icons/core";
import { lucideEye, lucideEyeOff } from "@ng-icons/lucide";

/**
 * Password Input Component
 *
 * A reusable password input component with show/hide toggle functionality.
 * Fully integrates with Angular Forms and Spartan UI components.
 */
@Component({
  selector: "app-password-input",
  standalone: true,
  imports: [
    HlmInput,
    HlmIcon,
    HlmButton,
    NgIcon,
  ],
  providers: [
    {
      provide: BrnFormFieldControl,
      useExisting: forwardRef(() => PasswordInputComponent),
    },
    provideIcons({ lucideEye, lucideEyeOff }),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="relative">
      <input
        hlmInput
        [type]="_inputType()"
        [placeholder]="placeholder()"
        [disabled]="disabled()"
        [class]="inputClass()"
        [value]="_value()"
        (input)="_onInput($event)"
        (blur)="_onTouched()"
        class="w-full pr-10"
      />
      <button
        type="button"
        hlmBtn
        variant="ghost"
        size="icon"
        class="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
        [disabled]="disabled()"
        (click)="_togglePasswordVisibility()"
        [attr.aria-label]="_isPasswordVisible() ? 'Hide password' : 'Show password'"
        tabindex="-1"
      >
        <ng-icon hlm [name]="_toggleIcon()" size="sm" />
      </button>
    </div>
  `,
})
export class PasswordInputComponent
  implements ControlValueAccessor, BrnFormFieldControl, DoCheck
{
  /**
   * Placeholder text for the input
   */
  public readonly placeholder = input<string>("");

  /**
   * Disabled state of the input
   */
  public readonly disabled = input<boolean>(false);

  /**
   * CSS class to apply to the input element
   */
  public readonly inputClass = input<ClassValue>("");

  /**
   * Current input value
   */
  protected readonly _value = signal<string>("");

  /**
   * Password visibility toggle state
   */
  protected readonly _isPasswordVisible = signal<boolean>(false);

  /**
   * Computed input type based on visibility state
   */
  protected readonly _inputType = computed(() =>
    this._isPasswordVisible() ? "text" : "password",
  );

  /**
   * Computed icon name based on visibility state
   */
  protected readonly _toggleIcon = computed(() =>
    this._isPasswordVisible() ? "lucideEyeOff" : "lucideEye",
  );

  /**
   * Reference to the NgControl (FormControl) if available
   * Using inject() with self option to get the control from the same element
   */
  public readonly ngControl = inject(NgControl, { self: true, optional: true });

  private readonly _defaultErrorStateMatcher = inject(ErrorStateMatcher);
  private readonly _parentForm = inject(NgForm, { optional: true });
  private readonly _parentFormGroup = inject(FormGroupDirective, {
    optional: true,
  });

  /**
   * Error state tracker for validation
   */
  private readonly _errorStateTracker: ErrorStateTracker;

  /**
   * Error state signal - used by hlm-form-field
   */
  public readonly errorState = computed(() =>
    this._errorStateTracker.errorState(),
  );

  /**
   * Internal error state for styling
   */
  protected readonly _state = linkedSignal<{ error: "auto" | true }>(() => ({
    error: "auto",
  }));

  private _onChange: (value: string) => void = () => {};
  private _onTouchedCallback: () => void = () => {};

  constructor() {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    this._errorStateTracker = new ErrorStateTracker(
      this._defaultErrorStateMatcher,
      this.ngControl,
      this._parentFormGroup,
      this._parentForm,
    );

    effect(() => {
      const error = this._errorStateTracker.errorState();
      untracked(() => {
        if (this.ngControl) {
          const shouldShowError =
            error &&
            this.ngControl.invalid &&
            (this.ngControl.touched || this.ngControl.dirty);
          this._errorStateTracker.errorState.set(
            shouldShowError ? true : false,
          );
          this._state.set({ error: shouldShowError ? true : "auto" });
        }
      });
    });
  }

  ngDoCheck(): void {
    this._errorStateTracker.updateErrorState();
  }

  writeValue(value: string | null): void {
    this._value.set(value ?? "");
  }

  registerOnChange(fn: (value: string) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouchedCallback = fn;
  }

  setDisabledState(_isDisabled: boolean): void {
    // Disabled state is managed through the input signal
  }

  protected _onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    this._value.set(value);
    this._onChange(value);
  }

  protected _onTouched(): void {
    this._onTouchedCallback();
  }

  protected _togglePasswordVisibility(): void {
    this._isPasswordVisible.update((visible) => !visible);
  }
}
